import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Container, Button } from 'react-bootstrap';
import axios from 'axios';

interface MarkdownViewerProps {
  file: string;
  handleNavigate: (fileName: string) => void;
}

function docFileFromHref(href?: string) {
  if (!href) return null;
  try {
    const url = new URL(href, window.location.origin);
    if (!url.pathname.endsWith('/show-md') && url.pathname !== '/show-md') return null;
    return url.searchParams.get('n');
  } catch {
    const match = href.match(/show-md\?n=([^#&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
}

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ file, handleNavigate }) => {
  const filePath = `devops-docs/${file}.md`;
  const [content, setContent] = useState('');
  const [buttonText, setButtonText] = useState('Copy Markdown');
  const [buttonDisabled, setButtonDisabled] = useState(false);

  useEffect(() => {
    setContent('');
    axios.get(filePath)
      .then((response) => {
        setContent(typeof response.data === 'string' ? response.data : String(response.data));
      })
      .catch((error) => {
        setContent(
          `Error loading markdown file:\n\n* \`${filePath}\`\n\n* \`${error}\``
        );
      });
  }, [filePath]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(content).then(() => {
      setButtonText('Copied');
      setButtonDisabled(true);
      setTimeout(() => {
        setButtonText('Copy Markdown');
        setButtonDisabled(false);
      }, 2000);
    });
  };

  return (
    <div className="main-content">
      <Container className="mb-5 mt-3">
        <Button
          className="mb-3 float-end"
          variant={buttonDisabled ? 'secondary' : 'primary'}
          onClick={copyToClipboard}
          disabled={buttonDisabled}
          style={{ minWidth: '160px' }}
        >{buttonText}</Button>
        {file !== '0-index' && (
          <Button variant="link" onClick={() => handleNavigate('0-index')}>← All docs</Button>
        )}
        <ReactMarkdown
          components={{
            a: ({ href, children }) => {
              const doc = docFileFromHref(href);
              if (doc) {
                return (
                  <a
                    href={`/show-md?n=${encodeURIComponent(doc)}`}
                    onClick={(event) => {
                      event.preventDefault();
                      handleNavigate(doc);
                    }}
                  >
                    {children}
                  </a>
                );
              }
              return <a href={href}>{children}</a>;
            }
          }}
        >{content}</ReactMarkdown>
      </Container>
    </div>
  );
};

const ShowMD = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const file = searchParams.get('n') || '0-index';

  const handleNavigate = (fileName: string) => {
    navigate(`/show-md?n=${fileName}`);
  };

  return <MarkdownViewer file={file} handleNavigate={handleNavigate} />;
};

export default ShowMD;
